<script setup lang="ts" generic="V extends GenericObject">
import { toTypedSchema } from '@vee-validate/yup';
/**
 * NoraForm is a wrapper component around all form validation.
 *
 * It requires the inserted components to use `useField` from vee-validate, so that it can track the form state and validation. Without these, the form will not work as expected.
 *
 * The core concept here is that NoraForm provides the form context to its children via scoped slots.
 * These scoped slots are accessible by the consumer of NoraForm, so that it will look something like this:
 *
 * @example
 * ```ts
 * <NoraForm
 *   :validation-schema="schema"
 *   :initial-values="initialValues"
 *   @form-submitted="saveOperation"
 *   v-slot="{ canSubmit, submitForm }"
 * >
 *   <SomeField name="title" />
 *   <SomeButton
 *     :disabled="!canSubmit"
 *     @click="submitForm"
 *   />
 * </NoraForm>
 *```
 * */
import type {
  FormContext,
  FormMeta,
  FormOptions,
  GenericObject,
} from 'vee-validate';
import { useForm } from 'vee-validate';
import { computed } from 'vue';
import type * as yup from 'yup';

/*
 * The NoraFormProps are typed this way in order to give autocomplete/intellisense on use.
 */
export interface NoraFormProps<V extends GenericObject> {
  /**
   * Represents a yup validation schema.
   *
   * The generic type V represents this imaginary validations schema, and can be used to ensure coherence between the validation schema and the initial values.
   * @example
   * ```ts
   * yup.object({
   *   name: yup.string().required().min(3),
   * });
   * ```
   */
  validationSchema: yup.ObjectSchema<V>;

  /**
   * Represents the initial values of the form. Simply:
   *
   * @example
   * ```ts
   * initialValues: {
   *  name: 'John Doe',
   * }
   * ```
   *
   * Read once at mount. For dynamic values use resetForm from slot.
   */
  initialValues: FormOptions<V>['initialValues'];
  validateOnMount?: boolean;
}

const props = withDefaults(defineProps<NoraFormProps<V>>(), {
  validateOnMount: true,
});
const emit = defineEmits<{
  /**
   * Emitted when the form is submitted and the values are valid.
   * The payload is the validated form values.
   *
   * ### Usage
   * ```ts
   * <NoraForm
   *   @form-submitted="saveOperation"
   *   ...
   * >
   * (your form/ui stuff goes here)
   * </NoraForm>
   * ```
   */
  formSubmitted: [values: V];
}>();

defineSlots<{
  default: (props: {
    formValues: V;
    formMeta: FormMeta<V>;
    canSubmit: boolean;
    submitForm: (e?: Event) => Promise<void>;
    validateForm: FormContext<V>['validate'];
    resetForm: FormContext<V>['resetForm'];
  }) => unknown;
}>();

const typedValidationSchema = computed(() => {
  return toTypedSchema(props.validationSchema);
});

const form = useForm<V>({
  validationSchema: typedValidationSchema,
  initialValues: props.initialValues,
  validateOnMount: props.validateOnMount,
});
const { meta: formMetadata, handleSubmit, resetForm, errors, validate } = form;

/*
See https://github.com/logaretm/vee-validate/issues/4630, for why we cannot simply use `meta.value.valid`
 */
const canSubmit = computed(() => {
  return (Object.keys(errors.value).length === 0);
});

const submitForm = handleSubmit((validatedValues) => {
  emit('formSubmitted', validatedValues);
});
</script>

<template>
  <slot
    :form-values="form.values"
    :form-meta="formMetadata"
    :can-submit="canSubmit"
    :submit-form="submitForm"
    :validate-form="validate"
    :reset-form="resetForm"
  />
</template>
